import React, { useEffect, useMemo } from 'react';
import NProgress from 'nprogress';
import { motion } from 'framer-motion';
import { makeStyles, createStyles } from '@material-ui/styles';
import {
  alpha, Theme, styled, useTheme,
} from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import { Logo } from './Logo';

const nprogressStyle = makeStyles((theme: Theme) => createStyles({
  '@global': {
    '#nprogress': {
      pointerEvents: 'none',
      '& .bar': {
        top: 0,
        left: 0,
        height: 2,
        width: '100%',
        position: 'fixed',
        zIndex: theme.zIndex?.snackbar,
        backgroundColor: theme.palette?.primary.main,
        boxShadow: `0 0 2px ${theme.palette?.primary.main}`,
      },
      '& .peg': {
        right: 0,
        opacity: 1,
        width: 100,
        height: '100%',
        display: 'block',
        position: 'absolute',
        transform: 'rotate(3deg) translate(0px, -4px)',
        boxShadow: `0 0 10px ${theme.palette?.primary.main}, 0 0 5px ${theme.palette?.primary.main}`,
      },
    },
  },
}));

type StyleProps = {
    theme: Theme,
    isFullScreen: boolean,
}

const RootStyle = styled('div')(({ theme, isFullScreen }: StyleProps) => ({
  position: isFullScreen ? 'fixed' : 'static',
  top: 0,
  left: 0,
  height: '100%',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background?.default,
}));

const ProgressBar = (): null => {
  nprogressStyle();

  useMemo(() => {
    NProgress.start();
  }, []);

  useEffect(() => {
    NProgress.done();
  }, []);

  return null;
};

type LoadingScreenProps = {
    isFullScreen?: boolean,
}

export const LoadingScreen:React.FC<LoadingScreenProps> = ({ isFullScreen = false }) => {
  const theme = useTheme();
  return (
    <RootStyle theme={theme} isFullScreen={isFullScreen}>
      <ProgressBar />

      <motion.div
        initial={{ rotateY: 0 }}
        animate={{ rotateY: 360 }}
        transition={{
          duration: 3,
          ease: 'easeInOut',
          repeatDelay: 1,
          repeat: Infinity,
        }}
      >
        <Logo sx={{ width: 64, height: 64 }} />
      </motion.div>

      <Box
        component={motion.div}
        animate={{
          scale: [1.2, 1, 1, 1.2, 1.2],
          rotate: [270, 0, 0, 270, 270],
          opacity: [0.25, 1, 1, 1, 0.25],
          borderRadius: ['25%', '25%', '50%', '50%', '25%'],
        }}
        transition={{ ease: 'linear', duration: 3.2, repeat: Infinity }}
        sx={{
          width: 100,
          height: 100,
          borderRadius: '25%',
          position: 'absolute',
          border: (t): string => `solid 3px ${alpha(t.palette.primary.dark, 0.24)}`,
        }}
      />

      <Box
        component={motion.div}
        animate={{
          scale: [1, 1.2, 1.2, 1, 1],
          rotate: [0, 270, 270, 0, 0],
          opacity: [1, 0.25, 0.25, 0.25, 1],
          borderRadius: ['25%', '25%', '50%', '50%', '25%'],
        }}
        transition={{
          ease: 'linear',
          duration: 3.2,
          repeat: Infinity,
        }}
        sx={{
          width: 120,
          height: 120,
          borderRadius: '25%',
          position: 'absolute',
          border: (t): string => `solid 8px ${alpha(t.palette.primary.dark, 0.24)}`,
        }}
      />
    </RootStyle>
  );
};
